import { create } from 'zustand'
import type { IParsedAttachmentType, ISceneEditMetadata } from '@/features/agents/utils/agent-parse-attachments-tag'

export type IPendingAgentAttachmentType = Exclude<IParsedAttachmentType, 'file'>

/** Input shape for constructing a pending attachment (before the store sets `addedAt`). */
export type IPendingAgentAttachmentPayload = Omit<IPendingAgentAttachment, 'addedAt'>

export interface IPendingAgentAttachment {
    type: IPendingAgentAttachmentType
    uuid: string
    title: string
    description?: string
    thumbnailUrl?: string
    /** `thumbnailUrl` points at a VIDEO file (raw MP4, not a still) — the chip renders a first-frame
     *  `<video>` poster instead of an `<Image>`. scene-edit chips imply this by type; a video-project
     *  draft attachment (fileless creative — its only pixels are the doc's clip assets) sets it
     *  explicitly. */
    thumbnailIsVideo?: boolean
    addedAt: number
    // Ad-specific
    adId?: string
    brandName?: string
    thumbnailFileUuid?: string
    // Generation-specific
    generationType?: string
    userPrompt?: string
    editRegions?: Array<{ rect: { x: number; y: number; width: number; height: number }; text: string }>
    // Generation variant-specific (selected aspect ratio variant)
    variantFileUuid?: string
    // Text-specific (inline content, no retrieval tool needed)
    textContent?: string
    // Actor-specific
    actorName?: string
    actorBaseImageFileUuid?: string
    // Scene-edit-specific (scene clip picked from a Studio timeline)
    sceneMetadata?: ISceneEditMetadata
}

interface IPendingAgentAttachmentStore {
    pendingAttachments: IPendingAgentAttachment[]
    /**
     * Attached UUIDs keyed by agent UUID.
     * Tracks which generations/ads/reports are currently attached to each agent's chat,
     * so the "Added" badge is scoped to the correct conversation.
     */
    attachedUuidsByAgent: Record<string, string[]>
    /**
     * UUIDs currently held in the chat input's local state, keyed by agent UUID.
     * Populated when the chat input consumes pending attachments, cleared when
     * the message is sent or the attachment is dismissed. Used by action buttons
     * (e.g. Iterate) to show a persistent success state while the attachment
     * is visible in the input.
     */
    inputUuidsByAgent: Record<string, string[]>
    /** Consumed-chip payloads per agent input — see {@link IPendingAgentAttachmentStore.stashInputPayloads}. */
    inputPayloadsByAgent: Record<string, IPendingAgentAttachment[]>
    /**
     * Pending prompt text keyed by agent UUID.
     * Set when a quick action (e.g. "Replicate") needs to pre-fill the chat input
     * alongside an attachment. Consumed once by the chat input.
     */
    pendingPromptByAgent: Record<string, string>
    /**
     * Persisted draft prompt keyed by real agent UUID. Continuously synced from
     * the composer so a typed-but-unsent prompt survives chat switches and
     * sibling-route navigation (context files, chats list, etc.) that unmount
     * `AgentChatContainer`. Empty-state new-chat composers do not persist here,
     * so drafts stay strictly scoped to their own agent chat.
     */
    draftPromptByAgent: Record<string, string>
    /**
     * Pending skill mentions keyed by agent UUID.
     * When set alongside a prompt, the chat input inserts these as rich
     * `skillMention` Tiptap nodes (pill/chip UI) after the prompt text.
     */
    pendingSkillMentionsByAgent: Record<string, Array<{ label: string }>>
    /**
     * A pending video timestamp anchor keyed by agent UUID. Set by the video-editor "comment at
     * <time>" button; the chat input inserts it as a `timeAnchor` pill (never opens the @ picker).
     */
    pendingTimeAnchorByAgent: Record<string, { label: string }>
    /**
     * The video variant the editor preview is currently showing, keyed by agent UUID. Sticky — set
     * and cleared by the preview (never consumed), so it holds across sends while the preview is
     * open and every message carries the `[viewing variant: <label>]` scope line. Set only for
     * projects with more than one active variant: one variant is not a choice, so it is not a scope.
     */
    viewingVideoVariantByAgent: Record<string, { projectUuid: string; variantLabel: string }>
    /**
     * Pending marketing-channel slug keyed by agent UUID.
     * Set when a new chat is started from a channel surface (e.g. the dashboard
     * channel page's "New chat") so the composer preselects that channel.
     */
    pendingChannelByAgent: Record<string, string>
    /**
     * Stay-on-page intent keyed by agent UUID. Set when a task intent (e.g. trend
     * recreate) wants the submit to start the chat in the background instead of
     * navigating to it, so the user keeps their browsing context.
     */
    pendingStayOnPageByAgent: Record<string, boolean>
    /**
     * Home-briefing recommendation id keyed by agent UUID.
     * Set when the user starts a dashboard plan step in chat; consumed when the
     * composer message is submitted so the step can be marked done.
     */
    pendingHomeBriefingRecommendationIdByAgent: Record<string, string>
    /**
     * Pending starting-mode id keyed by agent UUID.
     * Set when a new chat is started from a surface that needs the agent to begin
     * in a specific mode (e.g. the dashboard goal card's "Set goal via chat" button,
     * which requests the brand status-quo questionnaire mode). Consumed once by the
     * composer when it opens.
     */
    pendingStartingModeByAgent: Record<string, string>
    pendingPromptCategoryIdByAgent: Record<string, string>
    /**
     * Pending product mentions keyed by agent UUID.
     * Enqueued when the user picks products from a `searchProducts` result card;
     * the chat input consumes them once and inserts them as rich `productMention`
     * chips, so the next message references the products as `product://` entities.
     */
    pendingProductMentionsByAgent: Record<string, Array<{ uuid: string; name: string }>>
    /**
     * Product-mention removal requests keyed by agent UUID. Enqueued when the user
     * deselects a product on a `searchProducts` result card; the chat input consumes
     * them once and deletes the matching `@product` chip from the prompt box.
     */
    pendingProductMentionRemovalsByAgent: Record<string, string[]>
    /**
     * Live set of product-mention UUIDs currently in the chat input, keyed by agent.
     * Published by the chat input on every editor change so a `searchProducts` result
     * card reflects the true prompt-box selection (including manual chip deletion).
     */
    activeProductMentionsByAgent: Record<string, string[]>
    addPendingAttachment: (attachment: IPendingAgentAttachmentPayload, agentUuid: string | null) => void
    clearPendingAttachments: () => void
    /** Remove specific pending attachments by UUID (scoped removal instead of clearing all) */
    removePendingAttachments: (uuids: string[]) => void
    /** Remove a single attached UUID for a specific agent (e.g. when the user dismisses one attachment) */
    removeAttachedUuid: (uuid: string, agentUuid: string | null) => void
    /** Clear all attached UUIDs for a specific agent (e.g. when the user sends the message) */
    clearAttachedUuids: (agentUuid: string | null) => void
    /** Get the attached UUIDs for a specific agent */
    getAttachedUuids: (agentUuid: string | null) => string[]
    /** Mark UUIDs as held in the chat input for a specific agent */
    trackInputUuids: (uuids: string[], agentUuid: string | null) => void
    /** Remove a single UUID from the input tracking (e.g. when dismissed from input) */
    untrackInputUuid: (uuid: string, agentUuid: string | null) => void
    /** Clear all input-tracked UUIDs for a specific agent (e.g. when the message is sent) */
    clearInputUuids: (agentUuid: string | null) => void
    /** Stash the full payloads the chat input consumed, keyed like `inputUuidsByAgent` — the input's
     *  chip state is component-local and dies on a remount (page-load transitions remount the input
     *  several times), so a freshly-mounted input rehydrates its chips from here. Cleaned up by the
     *  same calls that untrack/clear the uuids (dismiss and send). */
    stashInputPayloads: (payloads: IPendingAgentAttachmentPayload[], agentUuid: string | null) => void
    /** The stashed consumed payloads for an agent's input (empty array when none). */
    getInputPayloads: (agentUuid: string | null) => IPendingAgentAttachment[]
    /** Set a pending prompt to pre-fill the chat input for a specific agent */
    setPendingPrompt: (prompt: string, agentUuid: string | null) => void
    /** Consume and return the pending prompt for a specific agent (returns null if none) */
    consumePendingPrompt: (agentUuid: string | null) => string | null
    /** Persist the current draft prompt for a specific agent (empty string clears the entry) */
    setDraftPrompt: (prompt: string, agentUuid: string | null) => void
    /** Read the persisted draft prompt for a specific agent (empty string when none) */
    getDraftPrompt: (agentUuid: string | null) => string
    /** Set pending skill mentions to be inserted as rich pills in the chat input */
    setPendingSkillMentions: (mentions: Array<{ label: string }>, agentUuid: string | null) => void
    /** Consume and return pending skill mentions for a specific agent */
    consumePendingSkillMentions: (agentUuid: string | null) => Array<{ label: string }> | null
    /** Set a pending video time anchor to be inserted as a pill in the chat input */
    setPendingTimeAnchor: (anchor: { label: string }, agentUuid: string | null) => void
    /** Consume and return the pending time anchor for a specific agent (returns null if none) */
    consumePendingTimeAnchor: (agentUuid: string | null) => { label: string } | null
    /** Set the variant the editor preview is showing for a specific agent (sticky — survives sends) */
    setViewingVideoVariant: (
        entry: { projectUuid: string; variantLabel: string },
        agentUuid: string | null
    ) => void
    /** Clear the viewing context for a specific agent (the preview closed, or lost its choice) */
    clearViewingVideoVariant: (agentUuid: string | null) => void
    /** Set a pending marketing-channel slug to preselect in the composer for a specific agent */
    setPendingChannel: (channelSlug: string, agentUuid: string | null) => void
    /** Consume and return the pending channel slug for a specific agent (returns null if none) */
    consumePendingChannel: (agentUuid: string | null) => string | null
    /** Mark the next composer submit for this agent as stay-on-page (no navigation) */
    setPendingStayOnPage: (agentUuid: string | null) => void
    /** Consume the stay-on-page intent for a specific agent (false if none) */
    consumePendingStayOnPage: (agentUuid: string | null) => boolean
    /** Set a pending home-briefing recommendation to complete when the chat is submitted */
    setPendingHomeBriefingRecommendationId: (recommendationId: string, agentUuid: string | null) => void
    /** Consume and return the pending home-briefing recommendation id (returns null if none) */
    consumePendingHomeBriefingRecommendationId: (agentUuid: string | null) => string | null
    /** Set a pending starting-mode id for the agent created next by the composer */
    setPendingStartingMode: (modeId: string, agentUuid: string | null) => void
    /** Consume and return the pending starting-mode id for a specific agent (returns null if none) */
    consumePendingStartingMode: (agentUuid: string | null) => string | null
    /** Set a pending prompt-category id (e.g. `research`, `static-ads`) to preselect in the composer suggestions */
    setPendingPromptCategoryId: (categoryId: string, agentUuid: string | null) => void
    /** Consume and return the pending prompt-category id for a specific agent (returns null if none) */
    consumePendingPromptCategoryId: (agentUuid: string | null) => string | null
    /** Enqueue product mentions for the chat input to insert as rich chips (deduped by uuid) */
    addPendingProductMentions: (products: Array<{ uuid: string; name: string }>, agentUuid: string | null) => void
    /** Consume and return pending product mentions for a specific agent */
    consumePendingProductMentions: (agentUuid: string | null) => Array<{ uuid: string; name: string }> | null
    /** Enqueue a product-mention removal so the chat input deletes its `@product` chip */
    requestRemoveProductMention: (uuid: string, agentUuid: string | null) => void
    /** Consume and return pending product-mention removals for a specific agent */
    consumePendingProductMentionRemovals: (agentUuid: string | null) => string[] | null
    /** Publish the live set of product-mention UUIDs in the chat input (no-op if unchanged) */
    setActiveProductMentions: (uuids: string[], agentUuid: string | null) => void
    /**
     * Draft-flash signal keyed by agent UUID.
     * Incremented when an external action pushes a draft prompt/attachment and wants
     * the chat input to briefly highlight in brand orange so the user notices the
     * change. Pure visual cue — no submit semantics.
     */
    draftFlashSignalByAgent: Record<string, number>
    /** Request a transient orange highlight on the chat input for a specific agent */
    requestDraftFlash: (agentUuid: string | null) => void
}

export const NO_AGENT_KEY = '__no_agent__'
export function getAgentKey(agentUuid: string | null): string {
    return agentUuid ?? NO_AGENT_KEY
}

export const usePendingAgentAttachmentStore = create<IPendingAgentAttachmentStore>()((set, get) => ({
    pendingAttachments: [],
    attachedUuidsByAgent: {},
    inputUuidsByAgent: {},
    inputPayloadsByAgent: {},
    pendingPromptByAgent: {},
    draftPromptByAgent: {},
    pendingSkillMentionsByAgent: {},
    pendingTimeAnchorByAgent: {},
    viewingVideoVariantByAgent: {},
    pendingChannelByAgent: {},
    pendingStayOnPageByAgent: {},
    pendingHomeBriefingRecommendationIdByAgent: {},
    pendingStartingModeByAgent: {},
    pendingPromptCategoryIdByAgent: {},
    pendingProductMentionsByAgent: {},
    pendingProductMentionRemovalsByAgent: {},
    activeProductMentionsByAgent: {},
    draftFlashSignalByAgent: {},
    addPendingAttachment: (attachment, agentUuid) =>
        set((state) => {
            const key = getAgentKey(agentUuid)
            const existing = state.attachedUuidsByAgent[key] ?? []

            // Avoid duplicates by UUID
            if (existing.includes(attachment.uuid)) return state

            return {
                pendingAttachments: [...state.pendingAttachments, { ...attachment, addedAt: Date.now() }],
                attachedUuidsByAgent: {
                    ...state.attachedUuidsByAgent,
                    [key]: [...existing, attachment.uuid],
                },
            }
        }),
    clearPendingAttachments: () => set({ pendingAttachments: [] }),
    removePendingAttachments: (uuids) =>
        set((state) => ({
            pendingAttachments: state.pendingAttachments.filter((a) => !uuids.includes(a.uuid)),
        })),
    removeAttachedUuid: (uuid, agentUuid) =>
        set((state) => {
            const key = getAgentKey(agentUuid)
            const existing = state.attachedUuidsByAgent[key]
            if (!existing) return state
            return {
                attachedUuidsByAgent: {
                    ...state.attachedUuidsByAgent,
                    [key]: existing.filter((id) => id !== uuid),
                },
            }
        }),
    clearAttachedUuids: (agentUuid) =>
        set((state) => {
            const key = getAgentKey(agentUuid)
            if (!state.attachedUuidsByAgent[key]?.length) return state
            const next = { ...state.attachedUuidsByAgent }
            delete next[key]
            return { attachedUuidsByAgent: next }
        }),
    getAttachedUuids: (agentUuid) => {
        const key = getAgentKey(agentUuid)
        return get().attachedUuidsByAgent[key] ?? []
    },
    trackInputUuids: (uuids, agentUuid) =>
        set((state) => {
            const key = getAgentKey(agentUuid)
            const existing = state.inputUuidsByAgent[key] ?? []
            const newUuids = uuids.filter((u) => !existing.includes(u))
            if (newUuids.length === 0) return state
            return {
                inputUuidsByAgent: {
                    ...state.inputUuidsByAgent,
                    [key]: [...existing, ...newUuids],
                },
            }
        }),
    untrackInputUuid: (uuid, agentUuid) =>
        set((state) => {
            const key = getAgentKey(agentUuid)
            const existing = state.inputUuidsByAgent[key]
            if (!existing) return state
            const payloads = state.inputPayloadsByAgent[key]
            return {
                inputUuidsByAgent: {
                    ...state.inputUuidsByAgent,
                    [key]: existing.filter((id) => id !== uuid),
                },
                ...(payloads
                    ? {
                          inputPayloadsByAgent: {
                              ...state.inputPayloadsByAgent,
                              [key]: payloads.filter((a) => a.uuid !== uuid),
                          },
                      }
                    : {}),
            }
        }),
    clearInputUuids: (agentUuid) =>
        set((state) => {
            const key = getAgentKey(agentUuid)
            if (!state.inputUuidsByAgent[key]?.length && !state.inputPayloadsByAgent[key]?.length) return state
            const next = { ...state.inputUuidsByAgent }
            delete next[key]
            const nextPayloads = { ...state.inputPayloadsByAgent }
            delete nextPayloads[key]
            return { inputUuidsByAgent: next, inputPayloadsByAgent: nextPayloads }
        }),
    stashInputPayloads: (payloads, agentUuid) =>
        set((state) => {
            if (payloads.length === 0) return state
            const key = getAgentKey(agentUuid)
            const existing = state.inputPayloadsByAgent[key] ?? []
            const existingUuids = new Set(existing.map((a) => a.uuid))
            const fresh = payloads.filter((a) => !existingUuids.has(a.uuid))
            if (fresh.length === 0) return state
            return {
                inputPayloadsByAgent: {
                    ...state.inputPayloadsByAgent,
                    [key]: [...existing, ...fresh.map((a) => ({ addedAt: Date.now(), ...a }))],
                },
            }
        }),
    getInputPayloads: (agentUuid) => get().inputPayloadsByAgent[getAgentKey(agentUuid)] ?? [],
    setPendingPrompt: (prompt, agentUuid) =>
        set((state) => ({
            pendingPromptByAgent: {
                ...state.pendingPromptByAgent,
                [getAgentKey(agentUuid)]: prompt,
            },
        })),
    consumePendingPrompt: (agentUuid) => {
        const key = getAgentKey(agentUuid)
        const prompt = get().pendingPromptByAgent[key] ?? null
        if (prompt !== null) {
            set((state) => {
                const next = { ...state.pendingPromptByAgent }
                delete next[key]
                return { pendingPromptByAgent: next }
            })
        }
        return prompt
    },
    setDraftPrompt: (prompt, agentUuid) =>
        set((state) => {
            const key = getAgentKey(agentUuid)
            const existing = state.draftPromptByAgent[key]
            if (!prompt) {
                if (existing === undefined) return state
                const next = { ...state.draftPromptByAgent }
                delete next[key]
                return { draftPromptByAgent: next }
            }
            if (existing === prompt) return state
            return {
                draftPromptByAgent: {
                    ...state.draftPromptByAgent,
                    [key]: prompt,
                },
            }
        }),
    getDraftPrompt: (agentUuid) => get().draftPromptByAgent[getAgentKey(agentUuid)] ?? '',
    setPendingSkillMentions: (mentions, agentUuid) =>
        set((state) => ({
            pendingSkillMentionsByAgent: {
                ...state.pendingSkillMentionsByAgent,
                [getAgentKey(agentUuid)]: mentions,
            },
        })),
    consumePendingSkillMentions: (agentUuid) => {
        const key = getAgentKey(agentUuid)
        const mentions = get().pendingSkillMentionsByAgent[key] ?? null
        if (mentions !== null) {
            set((state) => {
                const next = { ...state.pendingSkillMentionsByAgent }
                delete next[key]
                return { pendingSkillMentionsByAgent: next }
            })
        }
        return mentions
    },
    setPendingTimeAnchor: (anchor, agentUuid) =>
        set((state) => ({
            pendingTimeAnchorByAgent: {
                ...state.pendingTimeAnchorByAgent,
                [getAgentKey(agentUuid)]: anchor,
            },
        })),
    consumePendingTimeAnchor: (agentUuid) => {
        const key = getAgentKey(agentUuid)
        const anchor = get().pendingTimeAnchorByAgent[key] ?? null
        if (anchor !== null) {
            set((state) => {
                const next = { ...state.pendingTimeAnchorByAgent }
                delete next[key]
                return { pendingTimeAnchorByAgent: next }
            })
        }
        return anchor
    },
    setViewingVideoVariant: (entry, agentUuid) =>
        set((state) => {
            const key = getAgentKey(agentUuid)
            const prev = state.viewingVideoVariantByAgent[key]
            // Skip when unchanged so the preview's poll cycle doesn't churn the store ref and
            // re-render the composer on every refetch.
            if (prev?.projectUuid === entry.projectUuid && prev?.variantLabel === entry.variantLabel) {
                return state
            }
            return {
                viewingVideoVariantByAgent: {
                    ...state.viewingVideoVariantByAgent,
                    [key]: entry,
                },
            }
        }),
    clearViewingVideoVariant: (agentUuid) =>
        set((state) => {
            const key = getAgentKey(agentUuid)
            if (!state.viewingVideoVariantByAgent[key]) return state
            const next = { ...state.viewingVideoVariantByAgent }
            delete next[key]
            return { viewingVideoVariantByAgent: next }
        }),
    setPendingChannel: (channelSlug, agentUuid) =>
        set((state) => ({
            pendingChannelByAgent: {
                ...state.pendingChannelByAgent,
                [getAgentKey(agentUuid)]: channelSlug,
            },
        })),
    consumePendingChannel: (agentUuid) => {
        const key = getAgentKey(agentUuid)
        const channelSlug = get().pendingChannelByAgent[key] ?? null
        if (channelSlug !== null) {
            set((state) => {
                const next = { ...state.pendingChannelByAgent }
                delete next[key]
                return { pendingChannelByAgent: next }
            })
        }
        return channelSlug
    },
    setPendingStayOnPage: (agentUuid) =>
        set((state) => ({
            pendingStayOnPageByAgent: {
                ...state.pendingStayOnPageByAgent,
                [getAgentKey(agentUuid)]: true,
            },
        })),
    consumePendingStayOnPage: (agentUuid) => {
        const key = getAgentKey(agentUuid)
        const stayOnPage = get().pendingStayOnPageByAgent[key] ?? false
        if (stayOnPage) {
            set((state) => {
                const next = { ...state.pendingStayOnPageByAgent }
                delete next[key]
                return { pendingStayOnPageByAgent: next }
            })
        }
        return stayOnPage
    },
    setPendingHomeBriefingRecommendationId: (recommendationId, agentUuid) =>
        set((state) => ({
            pendingHomeBriefingRecommendationIdByAgent: {
                ...state.pendingHomeBriefingRecommendationIdByAgent,
                [getAgentKey(agentUuid)]: recommendationId,
            },
        })),
    consumePendingHomeBriefingRecommendationId: (agentUuid) => {
        const key = getAgentKey(agentUuid)
        const recommendationId = get().pendingHomeBriefingRecommendationIdByAgent[key] ?? null
        if (recommendationId !== null) {
            set((state) => {
                const next = { ...state.pendingHomeBriefingRecommendationIdByAgent }
                delete next[key]
                return { pendingHomeBriefingRecommendationIdByAgent: next }
            })
        }
        return recommendationId
    },
    setPendingStartingMode: (modeId, agentUuid) =>
        set((state) => ({
            pendingStartingModeByAgent: {
                ...state.pendingStartingModeByAgent,
                [getAgentKey(agentUuid)]: modeId,
            },
        })),
    consumePendingStartingMode: (agentUuid) => {
        const key = getAgentKey(agentUuid)
        const modeId = get().pendingStartingModeByAgent[key] ?? null
        if (modeId !== null) {
            set((state) => {
                const next = { ...state.pendingStartingModeByAgent }
                delete next[key]
                return { pendingStartingModeByAgent: next }
            })
        }
        return modeId
    },
    setPendingPromptCategoryId: (categoryId, agentUuid) =>
        set((state) => ({
            pendingPromptCategoryIdByAgent: {
                ...state.pendingPromptCategoryIdByAgent,
                [getAgentKey(agentUuid)]: categoryId,
            },
        })),
    consumePendingPromptCategoryId: (agentUuid) => {
        const key = getAgentKey(agentUuid)
        const categoryId = get().pendingPromptCategoryIdByAgent[key] ?? null
        if (categoryId !== null) {
            set((state) => {
                const next = { ...state.pendingPromptCategoryIdByAgent }
                delete next[key]
                return { pendingPromptCategoryIdByAgent: next }
            })
        }
        return categoryId
    },
    addPendingProductMentions: (products, agentUuid) =>
        set((state) => {
            const key = getAgentKey(agentUuid)
            const existing = state.pendingProductMentionsByAgent[key] ?? []
            const existingUuids = new Set(existing.map((p) => p.uuid))
            const additions = products.filter((p) => !existingUuids.has(p.uuid))
            if (additions.length === 0) return state
            return {
                pendingProductMentionsByAgent: {
                    ...state.pendingProductMentionsByAgent,
                    [key]: [...existing, ...additions],
                },
            }
        }),
    consumePendingProductMentions: (agentUuid) => {
        const key = getAgentKey(agentUuid)
        const mentions = get().pendingProductMentionsByAgent[key] ?? null
        if (mentions !== null) {
            set((state) => {
                const next = { ...state.pendingProductMentionsByAgent }
                delete next[key]
                return { pendingProductMentionsByAgent: next }
            })
        }
        return mentions
    },
    requestRemoveProductMention: (uuid, agentUuid) =>
        set((state) => {
            const key = getAgentKey(agentUuid)
            const existing = state.pendingProductMentionRemovalsByAgent[key] ?? []
            if (existing.includes(uuid)) return state
            return {
                pendingProductMentionRemovalsByAgent: {
                    ...state.pendingProductMentionRemovalsByAgent,
                    [key]: [...existing, uuid],
                },
            }
        }),
    consumePendingProductMentionRemovals: (agentUuid) => {
        const key = getAgentKey(agentUuid)
        const removals = get().pendingProductMentionRemovalsByAgent[key] ?? null
        if (removals !== null) {
            set((state) => {
                const next = { ...state.pendingProductMentionRemovalsByAgent }
                delete next[key]
                return { pendingProductMentionRemovalsByAgent: next }
            })
        }
        return removals
    },
    setActiveProductMentions: (uuids, agentUuid) =>
        set((state) => {
            const key = getAgentKey(agentUuid)
            const prev = state.activeProductMentionsByAgent[key] ?? []
            // Skip when unchanged so typing (which leaves mentions intact) doesn't
            // churn the store ref and re-render every product card.
            if (prev.length === uuids.length && prev.every((u, i) => u === uuids[i])) return state
            return {
                activeProductMentionsByAgent: {
                    ...state.activeProductMentionsByAgent,
                    [key]: uuids,
                },
            }
        }),
    requestDraftFlash: (agentUuid) =>
        set((state) => {
            const key = getAgentKey(agentUuid)
            return {
                draftFlashSignalByAgent: {
                    ...state.draftFlashSignalByAgent,
                    [key]: (state.draftFlashSignalByAgent[key] ?? 0) + 1,
                },
            }
        }),
}))

// Explicit non-hook alias for utility/non-react usage via getState/setState.
export const pendingAgentAttachmentStore = usePendingAgentAttachmentStore
